import { useEffect } from "react";
import { API } from "@/lib/api";

/** Injects a `<script type="application/ld+json">` tag into <head> with
 *  SoftwareApplication + live AggregateRating + top reviews. Google uses this
 *  to show a star-rating rich card under smartsnotes.in in search results.
 *
 *  Reads live rating/reviews from the public `/api/reviews?scope=app` endpoint. */
export default function StructuredData() {
  useEffect(() => {
    const scriptId = "smartsnotes-jsonld";
    let cancelled = false;

    const inject = (data) => {
      if (cancelled) return;
      let el = document.getElementById(scriptId);
      if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = scriptId;
        document.head.appendChild(el);
      }
      el.textContent = JSON.stringify(data);
    };

    const build = (agg) => {
      const base = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "Smart S Notes",
        alternateName: "Smart Notes AI",
        url: "https://smartsnotes.in",
        applicationCategory: "EducationalApplication",
        operatingSystem: "Web",
        description:
          "Long videos, meetings and PDFs turned into one easy-to-grab page — with formulas, diagrams, sticky reminders, MCQs, and flashcards — in under a minute.",
        offers: [
          {
            "@type": "Offer",
            name: "Free Trial",
            price: "0",
            priceCurrency: "INR",
            description: "1 free note generation",
          },
          {
            "@type": "Offer",
            name: "Standard Plan (Launch offer — 53% off)",
            price: "699",
            priceCurrency: "INR",
            description: "33 notes / month + purchasable extra credits. Launch price ₹699 (regular ₹1,482).",
          },
        ],
        featureList: [
          "Lecture / PDF / URL / YouTube / Photo input",
          "Structured notes with definitions, formulas, and diagrams",
          "Buzan-style curved-branch mind maps",
          "KaTeX math formula rendering",
          "Auto-generated possible exam questions",
          "Sticky reminders (mnemonic post-its)",
          "Handwritten mode with real notebook page look",
          "Export to Markdown, PDF, DOCX, and Anki (.apkg)",
        ],
      };
      if (agg && agg.total > 0 && agg.average > 0) {
        base.aggregateRating = {
          "@type": "AggregateRating",
          ratingValue: String(agg.average.toFixed(1)),
          ratingCount: String(agg.total),
          bestRating: "5",
          worstRating: "1",
        };
        if (Array.isArray(agg.items) && agg.items.length > 0) {
          base.review = agg.items.slice(0, 5).map((r) => ({
            "@type": "Review",
            reviewRating: {
              "@type": "Rating",
              ratingValue: String(r.rating),
              bestRating: "5",
              worstRating: "1",
            },
            author: { "@type": "Person", name: r.name || "Anonymous" },
            reviewBody: r.text || "",
            datePublished: r.created_at || undefined,
          }));
        }
      }
      inject(base);
    };

    // Inject the base card immediately so the DOM has it even if reviews fail.
    build(null);

    // Then upgrade with live rating.
    fetch(`${API}/reviews?scope=app&limit=5`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        build(data);
      })
      .catch(() => {
        /* keep the base card */
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return null;
}
